// ===========================================
// Routing Utilities
// ===========================================

/**
 * Get the correct dashboard path based on user role
 * SECURITY: Never default to admin - unknown roles must go to login
 */
export function getDashboardPath(
  role: string | undefined,
  schoolSlug: string,
  opts: { isStaff?: boolean } = {}
): string {
  // A staff login (a TEACHER or PARENT holding a staff role) lands on the office view
  // (roles 1b §5.15). Any staff login passes /admin's route guard, so this cannot loop.
  if (opts.isStaff && (role === 'TEACHER' || role === 'PARENT')) return `/${schoolSlug}/admin`;
  switch (role) {
    case 'ADMIN':
      return `/${schoolSlug}/admin`;
    case 'TEACHER':
      return `/${schoolSlug}/teacher`;
    case 'PARENT':
      return `/${schoolSlug}/parent`;
    case 'STUDENT':
      return `/${schoolSlug}/student`;
    default:
      // SECURITY FIX: Unknown/undefined roles redirect to login, not admin
      return `/${schoolSlug}/login`;
  }
}

/**
 * Extract school slug from URL pathname
 * Returns null if no slug is found
 */
export function extractSchoolSlugFromPath(pathname: string): string | null {
  // Pattern: /:schoolSlug/...
  const match = pathname.match(/^\/([a-z0-9-]+)/);
  return match ? match[1] : null;
}

/**
 * Validate school slug format
 * Slugs must be lowercase alphanumeric with hyphens only
 */
export function isValidSlugFormat(slug: string): boolean {
  return /^[a-z0-9-]+$/.test(slug);
}

/**
 * Route segments under `/:schoolSlug` that render WITHOUT a logged-in user.
 *
 * ⚠️ MUST stay in step with the "Public school routes (no auth)" block in `App.tsx`
 * (currently `App.tsx:178-191`). `routing.test.ts` reads that block out of `App.tsx`
 * and asserts parity, so adding a public route there without adding it here fails CI.
 *
 * Several of these carry a magic-link token IN THE URL — the token *is* the credential,
 * so an unauthenticated visitor is the normal case, not an error.
 */
const PUBLIC_ROUTE_SEGMENTS = [
  'login',
  'forgot-password',
  'reset-password',
  'set-password',
  'meet-and-greet',
  'enquiry',
  'events',
  'register',
  'invitation',
  'permission-slips/sign',
  'unsubscribe',
  'legal',
  // ('p' — the website builder's public pages — was removed 2026-09-16.)
] as const;

/**
 * Is this path a public (no-auth) page?
 *
 * Used to suppress the authenticated client's redirect-to-login on a 401. Without it, a
 * visitor holding a STALE token who opens a magic link is bounced to the login page before
 * the page can render: `AuthProvider` fires `/auth/me` on mount, the expired token 401s, the
 * refresh fails, and the interceptor does a hard `window.location.href` to login — replacing
 * a page that never needed auth in the first place.
 *
 * It self-heals (the failed attempt clears the tokens, so a second click works), which makes
 * it easy to dismiss as a one-off. It isn't: it hits precisely the families who have used the
 * portal before, and it reads to them as "the link is broken".
 *
 * ⚠️ Deliberately matches the SEGMENT AFTER the slug, not a bare `includes()`. A substring
 * test would let a school slugged e.g. `events-academy` mark every one of its pages public.
 */
export function isPublicPath(pathname: string): boolean {
  // Root-level public pages (no tenant context) — see `App.tsx:170-174`.
  if (/^\/(schools|waitlist|legal)(\/|$)/.test(pathname) || pathname === '/') return true;

  const slug = extractSchoolSlugFromPath(pathname);
  if (!slug) return false;

  const rest = pathname.slice(`/${slug}`.length).replace(/^\//, '');
  // `/:schoolSlug` itself is the school's public landing page (`SchoolProfileFallback`
  // on the tenant block's index route in `App.tsx`).
  if (!rest) return true;

  return PUBLIC_ROUTE_SEGMENTS.some(
    (seg) => rest === seg || rest.startsWith(`${seg}/`)
  );
}
